/**
 * DynamicZoneRenderer.tsx — Renders all sections belonging to a named zone.
 *
 * Two operating modes:
 *
 * 1. Profile + Sections mode (legacy):
 *    Provide `profile` and `sections`. Uses `profile.zones[zoneKey]` to resolve
 *    which sections belong to the zone, then renders each as a DynamicSectionCard.
 *
 * 2. EntityData mode (new):
 *    Provide `entityData`. Collects all fields whose `displayConfig.patient360Zone`
 *    matches `zoneKey`, sorted by `displayConfig.order`. Supports two render modes:
 *      - `renderMode="cards"` (default): wraps fields in a DynamicSectionCard
 *      - `renderMode="flat"`: renders bare label-value pairs in a grid (no card
 *        borders — use this when embedding inside an existing card/header)
 */

import React from 'react';
import type {
  DynamicEntityData,
  DynamicEntityResolvedField,
  DynamicEntitySectionData,
  Patient360PresentationProfile,
} from '@/modules/programs/types/dynamicEntityView';
import { DynamicSectionCard } from './DynamicSectionCard';

// ─── Props ────────────────────────────────────────────────────

interface DynamicZoneRendererBaseProps {
  zoneKey: string;
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
}

// Legacy: profile + sections
interface DynamicZoneRendererProfileMode extends DynamicZoneRendererBaseProps {
  profile: Patient360PresentationProfile;
  sections: DynamicEntitySectionData[];
  entityData?: never;
  renderMode?: 'cards';
}

// New: entityData
interface DynamicZoneRendererEntityMode extends DynamicZoneRendererBaseProps {
  entityData: DynamicEntityData;
  profile?: never;
  sections?: never;
  renderMode?: 'cards' | 'flat';
}

type DynamicZoneRendererProps = DynamicZoneRendererProfileMode | DynamicZoneRendererEntityMode;

// ─── Zone field extraction helper ────────────────────────────

function extractZoneFields(
  entityData: DynamicEntityData,
  zoneKey: string,
): DynamicEntityResolvedField[] {
  const fields: DynamicEntityResolvedField[] = [];
  for (const section of entityData.sections) {
    for (const field of section.fields) {
      if (
        field.visible &&
        field.displayConfig?.patient360Zone === zoneKey
      ) {
        fields.push(field);
      }
    }
  }
  return fields.sort(
    (a, b) => (a.displayConfig?.order ?? 0) - (b.displayConfig?.order ?? 0),
  );
}

// ─── Component ────────────────────────────────────────────────

export const DynamicZoneRenderer: React.FC<DynamicZoneRendererProps> = (props) => {
  const { zoneKey, columns = 2, className = '' } = props;

  // ── EntityData mode ────────────────────────────────────────
  if ('entityData' in props && props.entityData) {
    const { entityData, renderMode = 'cards' } = props;
    const zoneFields = extractZoneFields(entityData, zoneKey);

    if (zoneFields.length === 0) return null;

    if (renderMode === 'flat') {
      return (
        <div className={`patient-meta-grid ${className}`}>
          {zoneFields.map((field) => (
            <div key={field.fieldKey} className="meta-item">
              <span className="meta-label">{field.label}</span>
              <span className="meta-value">
                {field.displayValue || '—'}
              </span>
            </div>
          ))}
        </div>
      );
    }

    // cards mode: wrap in a synthetic section
    const syntheticSection: DynamicEntitySectionData = {
      sectionKey: `__zone_${zoneKey}`,
      title: '',
      fields: zoneFields,
    };
    return (
      <div className={className}>
        <DynamicSectionCard
          section={syntheticSection}
          columns={Math.min(columns, 3) as 1 | 2 | 3}
        />
      </div>
    );
  }

  // ── Legacy: profile + sections mode ───────────────────────
  const { profile, sections } = props as DynamicZoneRendererProfileMode;
  const sectionKeys = profile.zones?.[zoneKey] ?? [];

  if (sectionKeys.length === 0 && sections.length > 0) {
    // Fallback: render all sections when zone mapping is empty
    return (
      <div className={`space-y-3 ${className}`}>
        {sections.map((s) => (
          <DynamicSectionCard key={s.sectionKey} section={s} columns={Math.min(columns, 3) as 1 | 2 | 3} />
        ))}
      </div>
    );
  }

  const orderedSections = sectionKeys
    .map((key) => sections.find((s) => s.sectionKey === key))
    .filter(Boolean) as DynamicEntitySectionData[];

  if (orderedSections.length === 0) return null;

  return (
    <div className={`space-y-3 ${className}`}>
      {orderedSections.map((s) => (
        <DynamicSectionCard key={s.sectionKey} section={s} columns={Math.min(columns, 3) as 1 | 2 | 3} />
      ))}
    </div>
  );
};
